/**
 * Generic Modal — backdrop + dialog frame + focus trap + ESC/click-outside
 * close. Subtle scrim (no blur) so the field stays crisp underneath and
 * the live stream remains visually present while the modal is open.
 *
 * Children are responsible for their own header/footer; Modal just provides
 * the chrome (positioning, focus management, dismissal).
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { isTopModal, popModal, pushModal } from './modalStack';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Accessible label for the dialog. Required for screen readers. */
  ariaLabel: string;
  /** Optional larger frame width (default 560px). */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Pin the dialog to a fixed 80vh height. Children that should scroll need
   * `flex: 1 1 auto; overflow-y: auto; min-height: 0` so the dialog frame
   * stays put while content scrolls internally.
   */
  fixedHeight?: boolean;
  /** data-testid for the dialog node. Defaults to "modal". */
  testid?: string;
}

export default function Modal({
  open,
  onClose,
  children,
  ariaLabel,
  size = 'md',
  fixedHeight = false,
  testid = 'modal',
}: ModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const stackToken = useRef<object>({});

  // Register with the modal stack so doc-level handlers can ask "am I topmost?"
  useEffect(() => {
    if (!open) return undefined;
    const token = stackToken.current;
    pushModal(token);
    return () => popModal(token);
  }, [open]);

  // Capture the opener element + initial focus inside the dialog.
  useEffect(() => {
    if (!open) return undefined;
    opener.current = (document.activeElement as HTMLElement) ?? null;
    const dialog = dialogRef.current;
    if (dialog) {
      const focusable = dialog.querySelector<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }
    return () => {
      opener.current?.focus();
    };
  }, [open]);

  // ESC + tab focus trap. Gate on topmost-modal so a stacked PlayerModal
  // over an InningDetailModal doesn't accidentally close both (Codex Pass-3).
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (!isTopModal(stackToken.current)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      data-testid={`${testid}-backdrop`}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={styles.backdrop}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-testid={testid}
        className={`${styles.dialog} ${styles[`size${size}`] ?? ''} ${fixedHeight ? styles.fixedHeight : ''}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
